import React, { useCallback, useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@hooks";
import { RootState } from "@Store";
import {
  selectAuthChecked,
  selectIsSystemAdmin,
} from "@Store/AppUser/selectors";
import { selectIsAuthenticated } from "@Store/App/selectors";
import { appUserDataRequest } from "@Store/AppUser/actions";
import { getOrganizationByIdRequest } from "@Store/Organizations/actions";
import LoginPage from "@Pages/Login";
import SingleOrganizationView from "@Pages/OrganizationView";
import UsersManagementPage from "@Pages/AdminPages/UsersManagement";
import PendingUserRequestsPage from "@Pages/AdminPages/PendingUserRequests";
import InvitationsPage from "@Pages/AdminPages/Invitations";
import CompanyLegalPage from "@Pages/CompanyLegal";
import CorporatePage from "@Pages/Corporate";
import SelectOrganizationPage from "@Pages/OrganizationPageSelectMain";
import Header from "@Components/Header";
import MiniDrawer from "@Components/SideBar";
import SystemSideBar from "@Components/SystemSideBar";
import FooterPoweredByBlackpin from "@Components/Footer Powered By Blackpin/footer-powered-by-blackpin";
import theme from "@src/theme";
import SnackbarComponent from "@Components/SnackBarComponent";
import TeamsManagementPage from "@Pages/AdminPages/TeamsManagement";
import SettingsPage from "@Pages/AdminPages/Settings";
import ThemeCustomizer from "@Pages/ThemeCustomizer";
import OrganizationsPage from "@Pages/System/OrganizationsPage";
import AppsPage from "@Pages/System/AppsPage";
import AppPoliciesPage from "@Pages/System/AppPoliciesPage";

function SystemRoute({ children }: { children: React.ReactNode }) {
  const isSystemAdmin = useAppSelector(selectIsSystemAdmin);
  const authChecked = useAppSelector(selectAuthChecked);

  if (!authChecked) {
    return <Redirect to="/login" />;
  }

  if (!isSystemAdmin) {
    return <Redirect to="/select-organization" />;
  }

  return <>{children}</>;
}

function LoginSwitch() {
  return (
    <>
      <SnackbarComponent />
      <Switch>
        <Route path="/login" component={LoginPage} />
        <Redirect to="/login" />
      </Switch>
    </>
  );
}

function AuthChecking() {
  return (
    <>
      <SnackbarComponent />
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "black",
        }}
      >
        <CircularProgress color="warning" />
      </Box>
    </>
  );
}

function MainRouter() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isOrgSelected, setIsOrgSelected] = useState<string | null>(
    sessionStorage.getItem("selectedOrganization")
  );

  const { isAuthenticated, appUser, isSystemAdmin, authChecked } =
    useAppSelector((state: RootState) => ({
      isAuthenticated: selectIsAuthenticated(state),
      appUser: state.appUser,
      isSystemAdmin: selectIsSystemAdmin(state),
      authChecked: selectAuthChecked(state),
    }));

  const isSystemRoute = location.pathname.startsWith("/system");
  const showOrgShell = Boolean(isOrgSelected) && !isSystemRoute;
  const showSystemShell = isSystemRoute && isSystemAdmin;

  useEffect(() => {
    if (!isAuthenticated) {
      setIsOrgSelected(null);
      return;
    }
    if (authChecked || appUser.general.isLoading) {
      return;
    }
    dispatch(appUserDataRequest());
  }, [dispatch, isAuthenticated, authChecked, appUser.general.isLoading]);

  useEffect(() => {
    if (!isAuthenticated || !appUser.general.authChecked) {
      return;
    }
    if (isOrgSelected) {
      return;
    }
    if (appUser.general.data?.isSystemAdmin) {
      history.replace("/system/organizations");
    } else {
      history.replace("/select-organization");
    }
    // Intentionally run only when auth completes so we don't yank the user off org pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, appUser.general.authChecked]);

  useEffect(() => {
    const selectedOrg = sessionStorage.getItem("selectedOrganization");
    if (selectedOrg) {
      dispatch(getOrganizationByIdRequest(selectedOrg));
    }
  }, [dispatch, isOrgSelected]);

  const handleOrgSelect = useCallback((orgId: string) => {
    sessionStorage.setItem("selectedOrganization", orgId);
    setIsOrgSelected(orgId);
  }, []);

  const handleBackToAllOrganizations = useCallback(() => {
    sessionStorage.removeItem("selectedOrganization");
    setIsOrgSelected(null);
    history.push("/system/organizations");
  }, [history]);

  if (!authChecked) {
    if (isAuthenticated && (appUser.general.isLoading || !appUser.general.fail)) {
      return <AuthChecking />;
    }
    return <LoginSwitch />;
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#000 !important",
        backgroundColor: "black",
      }}
    >
      <SnackbarComponent />
      {showOrgShell && (
        <MemoizedHeader
          variant="org"
          onOrgSelect={handleOrgSelect}
          onBackToAllOrganizations={
            isSystemAdmin ? handleBackToAllOrganizations : undefined
          }
        />
      )}
      {showSystemShell && <MemoizedHeader variant="system" />}
      <div
        style={{
          width: "100%",
          display: "flex",
          height: "95%",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {showOrgShell && <MemoizedMiniDrawer />}
        {showSystemShell && <MemoizedSystemSideBar />}
        <div className="switch-container" style={{ flex: 1 }}>
          <Switch>
            <Route path="/system/organizations">
              <SystemRoute>
                <OrganizationsPage onOrgSelect={handleOrgSelect} />
              </SystemRoute>
            </Route>
            <Route path="/system/app-policies">
              <SystemRoute>
                <AppPoliciesPage />
              </SystemRoute>
            </Route>
            <Route path="/system/apps">
              <SystemRoute>
                <AppsPage />
              </SystemRoute>
            </Route>
            <Route
              path="/company-organizations/:id"
              component={SingleOrganizationView}
            />
            <Route path="/corporate" component={CorporatePage} />
            <Route path="/company-legal" component={CompanyLegalPage} />
            <Route path="/user-management" component={UsersManagementPage} />
            <Route
              path="/pending-user-requests"
              component={PendingUserRequestsPage}
            />
            {process.env.REACT_APP_SHOW_THEME_CUSTOMIZER === "true" && (
              <Route path="/theme-customizer" component={ThemeCustomizer} />
            )}
            <Route path="/invitations" component={InvitationsPage} />
            <Route path="/teams" component={TeamsManagementPage} />
            <Route path="/settings" component={SettingsPage} />

            <Route path="/select-organization">
              {!isOrgSelected ? (
                <SelectOrganizationPage onOrgSelect={handleOrgSelect} />
              ) : (
                <Redirect to="/corporate" />
              )}
            </Route>

            <Redirect
              from="*"
              to={
                isSystemAdmin && !isOrgSelected
                  ? "/system/organizations"
                  : "/select-organization"
              }
            />
          </Switch>
          {(showOrgShell || showSystemShell) && (
            <MemoizedFooterPoweredByBlackpin />
          )}
        </div>
      </div>
    </div>
  );
}

const MemoizedHeader = React.memo(Header);
const MemoizedMiniDrawer = React.memo(MiniDrawer);
const MemoizedSystemSideBar = React.memo(SystemSideBar);
const MemoizedFooterPoweredByBlackpin = React.memo(FooterPoweredByBlackpin);

export default MainRouter;
